"use client";

/**
 * The pre-signup diagnostic. Five questions, no account required.
 *
 * WHY THIS IS A DEDICATED ROUTE
 * Same lesson as /study/sample: the previous walkthrough lived inside the
 * 2000-line log page and fought that page's mutable form state. Here there
 * is no form. The whole flow is derived from an answers array, so most of
 * that class of bug is impossible by construction.
 *
 * WHY NOTHING TOUCHES THE NETWORK UNTIL SIGNUP
 * Questions ship as a static module and all state is local. That means no
 * unauthenticated write endpoint to abuse, no per-visitor cost, no rate
 * limiting to design, and no spinner. The first request happens only once
 * the visitor has an account.
 *
 * THE FAILURE THIS AVOIDS
 * Maren's existing sample walkthrough has converted 0 of 12 completers
 * into real loggers. Watching a demo built on someone else's data teaches
 * a student nothing about themselves. So here the questions are given but
 * the answers, the confidence, and the stated reasons are the user's own,
 * and the payoff speaks only about them.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useAuth } from "../contexts/AuthContext";
import {
  DIAGNOSTIC_QUESTIONS,
  cardsFor,
  isShaky,
  questionById,
  type Choice,
  type ConfidenceWord,
  type DiagnosticAnswer,
  type DiagnosticCard,
} from "../../lib/diagnosticQuestions";
import { findingLines, resultHeading } from "../../lib/diagnosticFindings";
import { ERROR_TAGS, ERROR_TAG_KEYS, type ErrorTag } from "../../lib/errorTags";
import { flushDiagnostic } from "../../lib/diagnosticApi";
import GuidedSetup from "./GuidedSetup";
import { resolveUserState, trackDiagnostic } from "../../lib/diagnosticAnalytics";
import type { DiagnosticUserState } from "../../lib/analytics";
import {
  clearSession,
  createSession,
  loadSession,
  recordAnswer,
  saveSession,
  type DiagnosticSession,
} from "../../lib/diagnosticSession";

const TOTAL = DIAGNOSTIC_QUESTIONS.length;
const MAX_REASONS = 2;

const CONF_OPTIONS: { key: ConfidenceWord; label: string }[] = [
  { key: "sure", label: "Sure" },
  { key: "fairly", label: "Fairly sure" },
  { key: "guessing", label: "Guessing" },
];

/** Renders {{...}} as visible Anki cloze syntax, numbered c1, c2, ... */
function ClozeText({ text }: { text: string }) {
  let n = 0;
  const parts = text.split(/(\{\{.+?\}\})/g).map((chunk, i) => {
    const m = chunk.match(/^\{\{(.+?)\}\}$/);
    if (!m) return <span key={i}>{chunk}</span>;
    n += 1;
    return (
      <span
        key={i}
        className="rounded bg-maren-green/10 dark:bg-maren-gold/15 px-1 py-0.5 font-medium"
      >{`{{c${n}::${m[1]}}}`}</span>
    );
  });
  return <>{parts}</>;
}

function FlashCard({ card, kind }: { card: DiagnosticCard; kind: "habit" | "concept" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-4 mt-2">
      <p className="text-xs text-maren-charcoal/60 dark:text-maren-stone/60 mb-1.5">
        {kind === "habit" ? "Why you missed it" : "The concept"}
        {card.format === "cloze" ? ", cloze" : ""}
      </p>
      {card.format === "cloze" ? (
        <p className="text-sm leading-relaxed">
          <ClozeText text={card.cloze} />
        </p>
      ) : (
        <>
          <p className="text-sm font-medium mb-2">{card.front}</p>
          {open ? (
            <p className="text-sm rounded-md bg-maren-stone/60 dark:bg-maren-night/40 p-3">
              {card.back}
            </p>
          ) : (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="text-sm font-medium text-maren-green dark:text-maren-gold hover:underline"
            >
              Show answer
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default function DiagnosticPage() {
  const { session: authSession } = useAuth();

  const [session, setSession] = useState<DiagnosticSession | null>(null);
  const [started, setStarted] = useState(false);
  /** An unfinished run found in storage. OFFERED on the intro screen, never
   *  forced: silently dropping someone into question 3 of a quiz they do not
   *  remember starting reads as a bug, not a convenience. */
  const [resumable, setResumable] = useState<DiagnosticSession | null>(null);
  const [picked, setPicked] = useState<Choice | null>(null);
  const [confidence, setConfidence] = useState<ConfidenceWord | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [reasons, setReasons] = useState<ErrorTag[]>([]);
  const [capHit, setCapHit] = useState(false);
  const [cardsOpen, setCardsOpen] = useState(false);
  /** Whether they chose the guided path from the result screen. */
  const [guided, setGuided] = useState(false);
  /** Segments every funnel event so cold visitors and dormant users never
   *  share a denominator. Starts anonymous and firms up once auth loads. */
  const [userState, setUserState] = useState<DiagnosticUserState>("anonymous");
  const [startedAt, setStartedAt] = useState<number>(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    void resolveUserState(Boolean(authSession)).then((st) => {
      if (cancelled) return;
      setUserState(st);
      trackDiagnostic(st, "diagnostic_view");
    });
    return () => {
      cancelled = true;
    };
  }, [authSession]);

  // Resume anything still in flight. A six-to-eight minute flow on mobile
  // gets backgrounded constantly; losing progress there loses the user.
  useEffect(() => {
    const existing = loadSession();
    if (!existing || existing.answers.length === 0) return;
    if (existing.answers.length >= TOTAL) {
      // Finished but never saved to an account: closing the tab must not
      // destroy a result they spent six minutes earning. Go straight back
      // to it. loadSession already refuses anything already flushed.
      setSession(existing);
      setStarted(true);
      return;
    }
    setResumable(existing);
  }, []);

  const answers = session?.answers ?? [];
  const cursor = answers.length;
  const question = cursor < TOTAL ? DIAGNOSTIC_QUESTIONS[cursor] : null;
  const complete = cursor >= TOTAL;

  // Save as soon as the run finishes, if they are already signed in.
  // DiagnosticFlush cannot cover this case: it keys off the auth session,
  // and finishing a quiz while already authenticated changes nothing about
  // that session, so its effect never re-fires and the diagnostic would sit
  // in localStorage until the user happened to navigate somewhere.
  // Fire the completion event exactly once, regardless of auth. Keyed on a
  // ref rather than state so a re-render cannot double-count a funnel step.
  const completionLogged = useRef(false);
  useEffect(() => {
    if (!complete || completionLogged.current) return;
    completionLogged.current = true;
    const correct = answers.filter((a) => {
      const q = questionById(a.id);
      return q && a.picked === q.correct;
    }).length;
    const lucky = answers.filter((a) => {
      const q = questionById(a.id);
      return q && a.picked === q.correct && a.confidence === "guessing";
    }).length;
    trackDiagnostic(userState, "diagnostic_completed", {
      correct,
      misses: answers.length - correct,
      lucky_guesses: lucky,
    });
  }, [complete, answers, userState]);

  useEffect(() => {
    if (!complete || !authSession) return;
    void flushDiagnostic().then((r) => {
      if (r.status === "saved") {
        trackDiagnostic(userState, "diagnostic_saved", {
          saved: r.saved,
          replaced: r.replaced,
        });
      }
    });
  }, [complete, authSession, userState]);

  /** Always a clean run. Any half-finished session is discarded here so
   *  "Start over" genuinely starts over. */
  const begin = useCallback(() => {
    clearSession();
    const s = createSession();
    setSession(s);
    setResumable(null);
    setStarted(true);
    setStartedAt(Date.now());
    trackDiagnostic(userState, "diagnostic_started", { resumed: false });
  }, [userState]);

  const resume = useCallback(() => {
    if (!resumable) return;
    setSession(resumable);
    setStarted(true);
    setStartedAt(Date.now());
    trackDiagnostic(userState, "diagnostic_started", { resumed: true });
  }, [resumable, userState]);

  const toggleReason = (tag: ErrorTag) => {
    setReasons((prev) => {
      if (prev.includes(tag)) {
        setCapHit(false);
        return prev.filter((t) => t !== tag);
      }
      if (prev.length >= MAX_REASONS) {
        // Make the constraint visible rather than swallowing the tap.
        setCapHit(true);
        return prev;
      }
      setCapHit(false);
      return [...prev, tag];
    });
  };

  const advance = () => {
    if (!session || !question || !picked || !confidence) return;
    const answer: DiagnosticAnswer = {
      id: question.id,
      picked,
      confidence,
      reasons,
      timeSpentSec: Math.max(0, Math.round((Date.now() - startedAt) / 1000)),
    };
    const next = recordAnswer(session, answer);
    setSession(next);
    saveSession(next);
    trackDiagnostic(userState, "diagnostic_question_answered", {
      question_id: question.id,
      position: cursor + 1,
      correct: picked === question.correct,
      lucky_guess: picked === question.correct && confidence === "guessing",
      reason_count: reasons.length,
      seconds: answer.timeSpentSec ?? null,
    });
    setPicked(null);
    setConfidence(null);
    setRevealed(false);
    setReasons([]);
    setCapHit(false);
    setCardsOpen(false);
    setStartedAt(Date.now());
    window.scrollTo({ top: 0 });
  };

  // A correct answer the student was guessing at still needs a reason:
  // they did not know it. It is reported as a lucky guess, never a miss.
  const wasWrong = question ? picked !== null && picked !== question.correct : false;
  const needsReason =
    question && picked !== null && confidence !== null
      ? isShaky({ id: question.id, picked, confidence, reasons })
      : false;

  const pendingCards = useMemo(() => {
    if (!question || !picked || !confidence || !reasons.length) return [];
    return cardsFor({ id: question.id, picked, confidence, reasons });
  }, [question, picked, confidence, reasons]);

  if (!started) {
    return (
      <main className="flex-1 px-4 py-12">
        <div className="mx-auto max-w-2xl">
          <h1 className="font-display text-3xl sm:text-4xl leading-tight">
            Why are you missing questions?
          </h1>
          <p className="mt-3 text-maren-charcoal/70 dark:text-maren-stone/70">
            Five questions. No account needed.
          </p>
          <div className="mt-8 rounded-xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-6">
            <p className="mb-3">
              Most tools stop at what you got wrong. Maren is about why.
            </p>
            <p className="mb-3">
              Answer five questions, say how sure you are on each one, then select
              why any of them went wrong. You will get a read on how you actually
              lose points.
            </p>
            <p className="text-sm text-maren-charcoal/60 dark:text-maren-stone/60">
              Original questions written in MCAT style. Not AAMC content.
            </p>
          </div>
          {resumable ? (
            <>
              <button
                type="button"
                onClick={resume}
                className="mt-6 w-full rounded-lg bg-maren-green hover:bg-maren-green/90 px-6 py-3.5 font-semibold text-maren-stone transition"
              >
                Pick up where you left off, question {resumable.answers.length + 1} of {TOTAL}
              </button>
              <button
                type="button"
                onClick={begin}
                className="mt-3 w-full rounded-lg border border-maren-sage/50 px-6 py-3 font-semibold transition hover:border-maren-green"
              >
                Start over
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={begin}
              className="mt-6 w-full rounded-lg bg-maren-green hover:bg-maren-green/90 px-6 py-3.5 font-semibold text-maren-stone transition"
            >
              Start
            </button>
          )}
        </div>
      </main>
    );
  }

  if (complete && guided) {
    return (
      <GuidedSetup
        answers={answers}
        signedIn={Boolean(authSession)}
        userState={userState}
        onBack={() => setGuided(false)}
      />
    );
  }

  if (complete) {
    return (
      <DiagnosticResult
        answers={answers}
        signedIn={Boolean(authSession)}
        onFree={() => trackDiagnostic(userState, "diagnostic_path_chosen", { path: "free" })}
        onGuided={() => {
          trackDiagnostic(userState, "diagnostic_path_chosen", { path: "guided" });
          setGuided(true);
          window.scrollTo({ top: 0 });
        }}
      />
    );
  }

  if (!question) return null;

  return (
    <main className="flex-1 px-4 py-8">
      <div className="mx-auto max-w-2xl">
        <p className="text-sm text-maren-charcoal/60 dark:text-maren-stone/60 mb-4">
          Question {cursor + 1} of {TOTAL}. {question.section}, {question.difficulty}.
        </p>

        <div className="rounded-xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-5 sm:p-6">
          <p className="whitespace-pre-line leading-relaxed">{question.stem}</p>

          <div className="mt-5 space-y-2">
            {(Object.keys(question.choices) as Choice[]).map((letter) => {
              const isPicked = picked === letter;
              const isCorrect = letter === question.correct;
              let tone =
                "border-maren-sage/40 dark:border-maren-sage/20 hover:border-maren-green";
              if (revealed && isCorrect) tone = "border-maren-green bg-maren-green/10";
              else if (revealed && isPicked) tone = "border-red-500 bg-red-500/10";
              else if (isPicked) tone = "border-maren-green bg-maren-green/10";
              return (
                <button
                  key={letter}
                  type="button"
                  disabled={revealed}
                  onClick={() => setPicked(letter)}
                  className={`block w-full rounded-lg border px-4 py-3 text-left transition ${tone} disabled:cursor-default`}
                >
                  <span className="inline-block w-6 text-maren-charcoal/50 dark:text-maren-stone/50">
                    {letter}
                  </span>
                  {question.choices[letter]}
                </button>
              );
            })}
          </div>

          {revealed && (
            <div
              className={`mt-4 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-line ${
                wasWrong
                  ? "bg-red-500/10"
                  : "bg-maren-green/10"
              }`}
            >
              <strong>
                {wasWrong ? `Not quite. The answer is ${question.correct}.` : "Correct."}
              </strong>
              {"\n\n"}
              {question.explanation}
            </div>
          )}
        </div>

        {/* Confidence is asked BEFORE the answer is shown, so it records
            what the student actually believed rather than hindsight. */}
        {picked && !revealed && (
          <div className="mt-4 rounded-xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-5">
            <p className="mb-3 text-maren-charcoal/70 dark:text-maren-stone/70">
              Before you see the answer, how sure are you?
            </p>
            <div className="flex flex-wrap gap-2">
              {CONF_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setConfidence(o.key)}
                  className={`flex-1 min-w-[104px] rounded-lg border px-4 py-2.5 text-sm transition ${
                    confidence === o.key
                      ? "border-maren-green bg-maren-green/10 font-semibold"
                      : "border-maren-sage/40 dark:border-maren-sage/20 hover:border-maren-green"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {revealed && needsReason && (
          <div className="mt-4 rounded-xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-5">
            <p className="mb-1 text-maren-charcoal/70 dark:text-maren-stone/70">
              {wasWrong
                ? "Select why you think you missed this."
                : "You got it, but you were guessing. Select what was missing."}{" "}
              Pick the 1 or 2 that fit best.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {ERROR_TAG_KEYS.map((tag) => {
                const on = reasons.includes(tag);
                const dim = reasons.length >= MAX_REASONS && !on;
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleReason(tag)}
                    className={`rounded-lg border px-3 py-2 text-sm transition ${
                      on
                        ? "border-maren-green bg-maren-green/10 font-semibold"
                        : "border-maren-sage/40 dark:border-maren-sage/20 hover:border-maren-green"
                    } ${dim ? "opacity-40" : ""}`}
                  >
                    {ERROR_TAGS[tag]}
                  </button>
                );
              })}
            </div>
            {capHit && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                Two at most. Deselect one to swap. Naming the main reasons tells
                Maren more than naming all of them.
              </p>
            )}

            {/* Compact affordance only. Two full cards after every miss turns
                a five-question diagnostic into homework, and a signup prompt
                here would pull people out before the payoff. */}
            {pendingCards.length > 0 && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setCardsOpen((v) => !v)}
                  className="flex w-full items-center justify-between rounded-lg border border-dashed border-maren-sage/50 px-4 py-3 text-left text-sm hover:border-maren-green transition"
                >
                  <span>
                    Maren made {pendingCards.length} review card
                    {pendingCards.length > 1 ? "s" : ""} from {wasWrong ? "this miss" : "this one"}
                  </span>
                  <span className="text-maren-charcoal/50 dark:text-maren-stone/50">
                    {cardsOpen ? "Hide" : "Open"}
                  </span>
                </button>
                {cardsOpen &&
                  pendingCards.map((c, i) => (
                    <FlashCard key={i} card={c.card} kind={c.kind} />
                  ))}
              </div>
            )}
          </div>
        )}

        {!revealed ? (
          <button
            type="button"
            disabled={!picked || !confidence}
            onClick={() => setRevealed(true)}
            className="mt-5 w-full rounded-lg bg-maren-green px-6 py-3.5 font-semibold text-maren-stone transition hover:bg-maren-green/90 disabled:opacity-30"
          >
            Check answer
          </button>
        ) : (
          <button
            type="button"
            disabled={needsReason && reasons.length === 0}
            onClick={advance}
            className="mt-5 w-full rounded-lg bg-maren-green px-6 py-3.5 font-semibold text-maren-stone transition hover:bg-maren-green/90 disabled:opacity-30"
          >
            {cursor === TOTAL - 1 ? "See my result" : "Next question"}
          </button>
        )}
      </div>
    </main>
  );
}

function DiagnosticResult({
  answers,
  signedIn,
  onGuided,
  onFree,
}: {
  answers: DiagnosticAnswer[];
  signedIn: boolean;
  onGuided: () => void;
  onFree: () => void;
}) {
  const correct = answers.filter((a) => {
    const q = questionById(a.id);
    return q && a.picked === q.correct;
  }).length;
  const misses = answers.filter((a) => {
    const q = questionById(a.id);
    return q && a.picked !== q.correct;
  });
  const lucky = answers.filter((a) => {
    const q = questionById(a.id);
    return q && a.picked === q.correct && a.confidence === "guessing";
  });
  const shaky = answers.filter(isShaky);
  const allCards = shaky.flatMap(cardsFor);
  const findings = findingLines(answers);

  // A lucky guess is a SUBSET of the correct answers, so it qualifies the
  // correct count rather than sitting beside it. Listing them in parallel
  // makes the numbers visibly sum to more than the question count.
  let head = `${correct} of ${answers.length} correct`;
  head += misses.length ? `, ${misses.length} miss${misses.length > 1 ? "es" : ""}.` : ".";
  if (lucky.length === 1) head += " One of those correct answers was a guess.";
  else if (lucky.length > 1) head += ` ${lucky.length} of those correct answers were guesses.`;

  return (
    <main className="flex-1 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <h1 className="font-display text-3xl leading-tight">{resultHeading(answers)}</h1>
        <p className="mt-4 text-lg">{head}</p>

        {/* The actual payoff. Without this the student gets a score, which
            is the one thing every other tool already gives them, and the
            "Maren is about why" promise from the intro goes unpaid. */}
        {findings.length > 0 && (
          <div className="mt-6 rounded-xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-5">
            {findings.map((line) => (
              <p key={line} className="text-[15px] first:mt-0 mt-2">
                {line}
              </p>
            ))}
          </div>
        )}

        <div className="mt-4 rounded-xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-5">
          <p className="text-sm text-maren-charcoal/60 dark:text-maren-stone/60">
            Read off five questions, so treat it as a starting point. Log twenty of
            your own and Maren can confirm the pattern.
          </p>
        </div>

        <h2 className="mt-8 font-display text-2xl">What do you want to do next?</h2>

        <div className="mt-4 rounded-xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-5">
          <h3 className="font-display text-lg mb-2">Track it yourself</h3>
          <p className="mb-4 text-sm">
            This result, your five questions, and your cards save to your account and
            stay yours. Keep logging your own misses and building your history.
          </p>
          <Link
            href={signedIn ? "/dashboard" : "/signup"}
            onClick={onFree}
            className="block w-full rounded-lg border border-maren-sage/50 px-6 py-3 text-center font-semibold transition hover:border-maren-green"
          >
            I want to track it myself
          </Link>
        </div>

        <div className="mt-4 rounded-xl bg-maren-charcoal dark:bg-maren-night p-6 text-maren-stone">
          <h2 className="font-display text-xl mb-2">Let Maren run it</h2>
          <p className="mb-4 text-sm text-maren-stone/75">
            Maren keeps watching these patterns as you log real practice, builds your
            week around them, and turns your misses into cards.
          </p>
          {/* Not straight to /pricing. A cold visitor does not care which
              tier they belong in, so the guided path asks the four planner
              inputs first and shows what Maren would actually run for THEM
              before naming a price. */}
          <button
            type="button"
            onClick={onGuided}
            className="block w-full rounded-lg bg-maren-stone px-6 py-3 text-center font-semibold text-maren-charcoal transition hover:bg-maren-stone/90"
          >
            I want Maren to guide my studying
          </button>
        </div>

        {allCards.length > 0 && (
          <div className="mt-6">
            <p className="text-sm text-maren-charcoal/60 dark:text-maren-stone/60">
              Maren already built {allCards.length} review card
              {allCards.length > 1 ? "s" : ""} from the questions you did not know,
              including the ones you guessed right.
            </p>
            {allCards.map((c, i) => (
              <FlashCard key={i} card={c.card} kind={c.kind} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
